/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Form as RFForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import GPSStatusAbsentIcon from '../../../assets/icons/GPSStatusAbsentIcon';
import GPSStatusPresentIcon from '../../../assets/icons/GPSStatusPresentIcon';
import KeyboardArrowDownIcon from '../../../assets/icons/KeyboardArrowDownIcon';
import workLocationPin from '../../../assets/icons/WorkLocationPin.svg';
import StatusNoteIcon from '../../../assets/status-note.svg';
import WaeButton, { BUTTON_TYPE } from '../../../components/Button';
import { buildFormField } from '../../../components/Form/formFieldFactory';
import GpsMapTrackPunches from '../../../components/Map/GpsMapTrackPunches';
import TextLink from '../../../components/TextLink/TextLink';
import selectUser from '../../../store/selectors/appSelector';
import { BLACK, PRIMARY_COLOR, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import {
  epochToDateInReadableFormat,
  epochToDayOfWeek,
  epochToTimeInReadableFormat,
} from '../../../utils';
import { ROUTE_CONSTANTS } from '../../app/RouteBuilding/routeConstants';
import { TIMECARD_UPDATE_FORM_FIELDS, TIMEKEEPING_STATUSES } from '../timekeepingConstants';
import { formatGpsData } from '../timekeepingHelpers';
import {
  postTimeCardUnFinalize,
  timecardEditV2,
  updateDisputedTimecard,
  updateTimekeepingDepartments,
} from '../timekeepingReducer';

import TimecardNotesModal from './TimeCardNotesModal/TimecardNotesModal';
import DisputeConfirmationModal from './DisputeConfirmationModal';
import WorkerTimecardDepartmentRow from './WorkedTimecardDepartmentRow';
import WorkerTimecardLastDepartmentRow from './WorkedTimecardLastDepartmentRow';

const departmentAPICallback = (corpId, departmentId) => ({
  httpMethod: 'POST',
  route: 'corporations/departments/read',
  generateBody: (searchTerm) => {
    const filters =
      departmentId?.length > 0
        ? [{ operation: 'equalsID', field: '_id', value: departmentId }]
        : [searchTerm && { operation: 'icontains', field: 'name', value: searchTerm }].filter(
            (i) => i
          );
    return {
      ...(corpId && { corporation: corpId }),
      filters,
    };
  },
});

const MAX_DEPARTMENT_COUNT = 10000;
const UNIT_EPOCH_TO_HRS = 3600 * 1000;

const WorkerTimecard = ({
  isChecked,
  onTimecardChecked,
  weekday,
  jobName,
  candidateName,
  shiftStart,
  shiftEnd,
  gps,
  attendance,
  timecard,
  placementId,
  corporationId,
  jobId,
  isDisputedTimecardUpdating,
}) => {
  const user = useSelector(selectUser);
  const canAcceptAndUpdate = user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYER;
  const jobPath = jobId
    ? generatePath(`/${ROUTE_CONSTANTS.JOB_ORDERS}/:id`, { id: jobId })
    : undefined;
  const timecardHistoryPath = placementId
    ? generatePath(`/${ROUTE_CONSTANTS.TIMECARD_HISTORY}`, { id: placementId })
    : undefined;
  const [isGpsMapOpen, setIsGpsMapOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState(
    timecard?.departments?.map((d) => ({
      ...d,
      allocatedTime: d.allocatedTime / UNIT_EPOCH_TO_HRS,
    })) || []
  );

  const calculatedTotalHours =
    (timecard?.punchOut && timecard?.punchIn
      ? timecard.punchOut - timecard.punchIn - (timecard.break ?? 0)
      : 0) / UNIT_EPOCH_TO_HRS;

  const editableDepartments = selectedDepartments.slice(0, -1);

  const lastDepartmentIndex = selectedDepartments.length - 1;

  const timecardStatusMap = {
    pending: LanguageConverterV2('timekeeping.workerTimecard.status.pending'),
    approved: LanguageConverterV2('timekeeping.workerTimecard.status.approved'),
    resolved: LanguageConverterV2('timekeeping.workerTimecard.status.resolved'),
    disputed: LanguageConverterV2('timekeeping.workerTimecard.status.disputed'),
    finalized: LanguageConverterV2('timekeeping.workerTimecard.status.finalized'),
  };

  const gpsStatusMap = {
    present: LanguageConverterV2('timekeeping.workerTimecard.gpsStatus.present'),
    ncns: LanguageConverterV2('timekeeping.workerTimecard.gpsStatus.ncns'),
    noGPSData: LanguageConverterV2('timekeeping.workerTimecard.gpsStatus.noGPSData'),
    partialShift: LanguageConverterV2('timekeeping.workerTimecard.gpsStatus.partialShift'),
  };

  const attendanceStatusMap = {
    present: LanguageConverterV2('timekeeping.workerTimecard.attendance.present'),
    'sent-home': LanguageConverterV2('timekeeping.workerTimecard.attendance.sentHome'),
    'no-show': LanguageConverterV2('timekeeping.workerTimecard.attendance.noShow'),
  };

  const isTimecardFinalized = timecard.status === TIMEKEEPING_STATUSES.FINALIZED;

  const dispatch = useDispatch();

  const handleNoteIconOnClick = () => {
    setIsNoteModalOpen(true);
  };
  const handleModalOkayOnClick = () => {
    setIsNoteModalOpen(false);
  };

  const isValidDepartments = useCallback(() => {
    const departmentIds = selectedDepartments.map((d) => d.departmentId);
    const uniqueDepartmentIds = new Set(departmentIds);
    return (
      selectedDepartments?.[lastDepartmentIndex]?.allocatedTime >= 0 &&
      uniqueDepartmentIds.size === departmentIds.length &&
      selectedDepartments?.every((d, inx) =>
        selectedDepartments.length - 1 !== inx
          ? d?.departmentId && d?.allocatedTime
          : !!d?.departmentId
      )
    );
  }, [selectedDepartments, lastDepartmentIndex]);

  const calcLastDepartmentAllocatedTime = useCallback(
    (allocatedTimes) =>
      Math.max(
        calculatedTotalHours -
          allocatedTimes.reduce((acc, curr) => acc + (curr?.allocatedTime ?? 0), 0),
        0
      ),
    [calculatedTotalHours]
  );

  const lastDepartment = {
    ...selectedDepartments[lastDepartmentIndex],
    allocatedTime: calcLastDepartmentAllocatedTime(editableDepartments),
  };

  const handleDisputeSubmit = useCallback(() => {
    dispatch(updateDisputedTimecard({ placementId }));
  }, [dispatch, placementId]);

  const handleTimecardUnFinalize = useCallback(() => {
    dispatch(postTimeCardUnFinalize({ placements: [placementId] }));
  }, [dispatch, placementId]);

  const handleTimecardUpdate = useCallback(
    (newVal) => {
      const punchIn = newVal?.punches?.[0]?.in?.stamp;
      const punchOut = newVal?.punches?.[0]?.out?.stamp;
      const payload = {
        placementId,
        punches: !punchIn && !punchOut ? [] : [{ in: punchIn, out: punchOut }],
        breakDelta: parseInt(newVal.breakDelta * 60 * 1000, 10),
      };
      dispatch(timecardEditV2(payload));
    },
    [dispatch, placementId]
  );
  const handleDepartmentsUpdate = useCallback(() => {
    const payload = {
      placementId,
      departments: selectedDepartments.map((d, inx) => ({
        departmentId: d.departmentId,
        allocatedTime: Math.floor(
          (selectedDepartments.length === inx + 1 ? 0 : d.allocatedTime) * UNIT_EPOCH_TO_HRS
        ),
        isAllocatedRemainingTime: selectedDepartments.length === inx + 1,
      })),
    };

    dispatch(updateTimekeepingDepartments(payload));
  }, [dispatch, placementId, selectedDepartments]);

  const handleDeleteDepartment = useCallback(
    (index) => {
      setSelectedDepartments((prev) => {
        const updatedDepartments = prev.filter((d, inx) => index !== inx);
        const lastDepartmentInx = updatedDepartments.length - 1;
        updatedDepartments[lastDepartmentInx] = {
          ...updatedDepartments[lastDepartmentInx],
          allocatedTime: calcLastDepartmentAllocatedTime(
            updatedDepartments.slice(0, lastDepartmentInx)
          ),
        };
        return updatedDepartments;
      });
    },
    [calcLastDepartmentAllocatedTime]
  );

  const handleDepartmentChange = useCallback((department, index) => {
    setSelectedDepartments((prev) =>
      prev.map((d, inx) => (inx === index ? { ...d, departmentId: department } : d))
    );
  }, []);

  const handleAllocatedTimeChange = useCallback((oldValue, newValue, index) => {
    setSelectedDepartments((prev) =>
      prev.map((d, inx) => (inx === index ? { ...d, allocatedTime: newValue } : d))
    );
  }, []);

  const getDisputedTotalHours = (start, end, brTime) => {
    const diff = end - start - brTime;
    const hours = diff / (1000 * 60 * 60);
    return hours;
  };
  const handleGpsMapOpen = () => {
    setIsGpsMapOpen(true);
  };

  return (
    <Stack gap="16px">
      <Box display="flex" alignItems="center">
        <Checkbox checked={!!isChecked} onChange={(e) => onTimecardChecked(e.target.checked)} />
        <Stack>
          <Box display="flex" flexDirection="row" gap="8px">
            <Typography sx={{ fontFamily: PRIMARY_FONT[600] }}>
              {epochToDayOfWeek(weekday)} {epochToDateInReadableFormat(weekday)} -
            </Typography>
            <TextLink
              to={jobPath}
              sx={{ color: PRIMARY_PURPLE[70], textDecoration: 'underline', cursor: 'pointer' }}
            >
              {jobName}
            </TextLink>
            <Typography>
              {epochToTimeInReadableFormat(shiftStart)} - {epochToTimeInReadableFormat(shiftEnd)}
            </Typography>
          </Box>
          <Typography fontSize="12px" fontFamily={PRIMARY_FONT[600]}>
            {timecardStatusMap?.[timecard?.status] || '--'}
          </Typography>
        </Stack>
      </Box>
      <Box display="flex" gap="24px">
        <RFForm
          onSubmit={handleTimecardUpdate}
          // Set the default start/end dates to match the dates of the expected start/end of the shift
          initialValues={{
            punches: [
              {
                in: { stamp: timecard?.punchIn },
                out: { stamp: timecard?.punchOut },
                jobStart: shiftStart,
                jobEnd: shiftEnd,
              },
            ],
            breakDelta: (timecard?.break ?? 0) / 60 / 1000,
          }}
          render={({ handleSubmit, invalid, pristine, submitting }) => (
            <form onSubmit={handleSubmit}>
              <Grid container direction="row" gap="20px">
                <Box width="500px">{buildFormField(TIMECARD_UPDATE_FORM_FIELDS.punches)}</Box>
                <Box sx={{ pt: '16px' }}>{buildFormField(TIMECARD_UPDATE_FORM_FIELDS.break)}</Box>

                <Box sx={{ pt: '16px' }}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {LanguageConverterV2('timekeeping.workerTimecard.dailyHrs')}
                  </Typography>
                  <Typography sx={{ color: PRIMARY_COLOR[50] }}>
                    {timecard?.totalHours} {LanguageConverterV2('timekeeping.workerTimecard.hrs')}
                  </Typography>
                </Box>

                {isTimecardFinalized && user?.role === UserRole.ADMIN ? (
                  <WaeButton onClick={handleTimecardUnFinalize} sx={{ mt: '20px' }}>
                    {LanguageConverterV2('buttonText.unFinalize')}
                  </WaeButton>
                ) : (
                  (user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYER) && (
                    <WaeButton
                      type={BUTTON_TYPE.SUBMIT}
                      disabled={invalid || pristine || submitting || isTimecardFinalized}
                      sx={{ mt: '20px' }}
                    >
                      {LanguageConverterV2('button.update')}
                    </WaeButton>
                  )
                )}
              </Grid>
            </form>
          )}
        />
        <Stack height="60px" justifyContent="space-between" mt="8px">
          <Typography sx={{ fontWeight: 'bold' }}>
            {LanguageConverterV2('timekeeping.workerTimecard.gpsStatus')}
          </Typography>
          <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
            {gps ? <GPSStatusPresentIcon /> : <GPSStatusAbsentIcon />}
            <Typography
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleGpsMapOpen}
            >
              {gpsStatusMap?.[gps?.status] || '--'}
            </Typography>
            <GpsMapTrackPunches
              open={isGpsMapOpen}
              title={candidateName}
              date={shiftStart}
              handleOnClose={() => setIsGpsMapOpen(false)}
              punchAndPinCoordinates={formatGpsData(gps)}
              workCoordinates={{
                lat: gps?.gpsTarget?.coordinates[1],
                lng: gps?.gpsTarget?.coordinates[0],
                icon: workLocationPin,
                name: LanguageConverterV2('timekeeping.workerTimecard.gps.workLocation'),
                _id: 'workLocation',
              }}
            />
          </Box>
        </Stack>
        <Stack height="60px" justifyContent="space-between" mt="8px">
          <Typography sx={{ fontWeight: 'bold' }}>
            {LanguageConverterV2('timekeeping.workerTimecard.attendance')}
          </Typography>
          <Grid container item direction="row">
            {(attendance?.leftEarlyReason?.note || attendance?.sentHomeReason?.note) && (
              <Box
                component="img"
                sx={{ height: 18, width: 18, marginRight: 1, cursor: 'pointer' }}
                alt="Status Note"
                src={StatusNoteIcon}
                onClick={handleNoteIconOnClick}
              />
            )}
            <Typography>
              {attendanceStatusMap?.[attendance?.status] ||
                LanguageConverterV2('timekeeping.workerTimecard.attendance.notMarked')}
            </Typography>
          </Grid>
        </Stack>
        <Stack height="60px" justifyContent="space-between" mt="8px">
          <Typography sx={{ fontWeight: 'bold' }}>
            {LanguageConverterV2('timekeeping.workerTimecard.timecardHistory')}
          </Typography>
          <TextLink
            to={timecardHistoryPath}
            sx={{ color: PRIMARY_PURPLE[70], textDecoration: 'underline', cursor: 'pointer' }}
          >
            {LanguageConverterV2('timekeeping.workerTimecard.timecardHistoryView')}
          </TextLink>
        </Stack>
        {timecard?.status === TIMEKEEPING_STATUSES.DISPUTED && (
          <Stack height="50px" justifyContent="end" mt="8px">
            <DisputeConfirmationModal
              onSubmit={handleDisputeSubmit}
              isProcessing={isDisputedTimecardUpdating}
              canAcceptAndUpdate={canAcceptAndUpdate}
              currentClockIn={timecard?.punchIn}
              currentClockOut={timecard?.punchOut}
              currentBreak={timecard?.break}
              currentTotalTime={timecard?.totalHours}
              disputedClockIn={timecard?.dispute?.clockIn}
              disputedClockOut={timecard?.dispute?.clockOut}
              disputedBreak={timecard?.dispute?.break?.breakTime}
              disputedTotalTime={getDisputedTotalHours(
                timecard?.dispute?.clockIn,
                timecard?.dispute?.clockOut,
                timecard?.dispute?.break?.breakTime
              )}
              reason={timecard?.dispute?.disputeReason}
            />
          </Stack>
        )}
      </Box>
      <Box>
        <Accordion
          expanded={isAccordionExpanded}
          onChange={(event, expanded) => setIsAccordionExpanded(expanded)}
          sx={{ boxShadow: 'none', justifyItems: 'start' }}
        >
          <AccordionSummary
            aria-controls="department-content"
            id="department-header"
            expandIcon={<KeyboardArrowDownIcon />}
            sx={{
              flexGrow: 'unset',
              gap: '12px',
              padding: 0,
              fontSize: '16px',
              fontFamily: PRIMARY_FONT[500],
            }}
          >
            {LanguageConverterV2('timekeeping.workerTimecard.departmentAssignment')}
          </AccordionSummary>
          <AccordionDetails
            sx={{ minWidth: '200px', minHeight: '200px', border: `1px ${BLACK[40]} solid` }}
          >
            {isAccordionExpanded && (
              <Stack gap="8px" sx={{ padding: '8px' }}>
                <Box>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setSelectedDepartments((prev) => [
                        {
                          departmentId: null,
                          allocatedTime: 0,
                          key: MAX_DEPARTMENT_COUNT - (prev?.length || 0),
                        },
                        ...prev,
                      ]);
                    }}
                  >
                    +{LanguageConverterV2('timekeeping.workerTimecard.addDepartment')}
                  </Link>
                </Box>
                <Stack gap="16px">
                  {editableDepartments?.map((department, index) => (
                    <WorkerTimecardDepartmentRow
                      key={department.key}
                      department={department}
                      onAllocatedTimeChange={(oldV, newV) =>
                        handleAllocatedTimeChange(oldV, newV, index)
                      }
                      onDelete={() => handleDeleteDepartment(index)}
                      onDepartmentChange={(d) => handleDepartmentChange(d, index)}
                      corporationId={corporationId}
                      departmentAPICallback={departmentAPICallback}
                    />
                  ))}
                  {lastDepartmentIndex > -1 && (
                    <WorkerTimecardLastDepartmentRow
                      isLastDepartment
                      corporationId={corporationId}
                      department={lastDepartment}
                      onDepartmentChange={(d) => handleDepartmentChange(d, lastDepartmentIndex)}
                      onUpdate={handleDepartmentsUpdate}
                      isValidDepartments={isValidDepartments()}
                      departmentAPICallback={departmentAPICallback}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      <TimecardNotesModal
        isOpen={isNoteModalOpen}
        attendance={attendance}
        handleOnClick={handleModalOkayOnClick}
      />
    </Stack>
  );
};

WorkerTimecard.propTypes = {
  isChecked: PropTypes.bool,
  onTimecardChecked: PropTypes.func,
  weekday: PropTypes.number,
  jobName: PropTypes.string,
  candidateName: PropTypes.string,
  jobId: PropTypes.string,
  shiftStart: PropTypes.number,
  shiftEnd: PropTypes.number,
  gps: PropTypes.shape({
    status: PropTypes.string,
    gpsPings: PropTypes.arrayOf(PropTypes.shape({})),
    gpsPunchIn: PropTypes.shape({}),
    gpsPunchOut: PropTypes.shape({}),
    gpsTarget: PropTypes.shape({
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
  }),
  attendance: PropTypes.shape({
    status: PropTypes.string,
    leftEarlyReason: PropTypes.shape({
      note: PropTypes.string,
    }),
    sentHomeReason: PropTypes.shape({ note: PropTypes.string }),
  }),
  timecard: PropTypes.shape({
    departments: PropTypes.arrayOf(
      PropTypes.shape({
        departmentId: PropTypes.string,
        name: PropTypes.string,
        allocatedTime: PropTypes.number,
      })
    ),
    punchIn: PropTypes.number,
    punchOut: PropTypes.number,
    break: PropTypes.number,
    totalHours: PropTypes.number,
    status: PropTypes.string,
    dispute: PropTypes.shape({
      clockIn: PropTypes.number,
      clockOut: PropTypes.number,
      break: PropTypes.shape({ breakTime: PropTypes.number }),
      disputeReason: PropTypes.string,
      totalHours: PropTypes.number,
    }),
  }),
  placementId: PropTypes.string,
  corporationId: PropTypes.string,
  isDisputedTimecardUpdating: PropTypes.bool,
};

export default WorkerTimecard;
